import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FormattedMessage } from '../../../../util/reactIntl';
import { useParams } from 'react-router-dom';
import {
  parseDayEntry,
  parseDaysFromActivitiesText,
} from './weeklyPlanUtils';
import css from './WeeklyPlan.module.css';

const FALLBACK_GERMAN_TEXT = 'Übersetzung hinzufügen';

const DayTitle = ({ day }) => {
  if (day.label) {
    return day.label;
  }

  return (
    <FormattedMessage
      id="WeeklyPlan.dayTitle"
      values={{
        day: day.dayNumber,
        title: day.titlePart || '',
      }}
    />
  );
};

const WeeklyPlan = ({
  listing,
  currentLanguage = 'de',
  embedded = false,
  activitiesText = '',
}) => {
  const { lang } = useParams();

  const programDetails = listing?.attributes?.publicData?.programDetails;
  const programDetailsEntries = programDetails?.details;
  const startInfo =
    currentLanguage === 'de'
      ? programDetails?.startInfo_de || programDetails?.startInfo
      : programDetails?.startInfo || programDetails?.startInfo_de;

  const getTitleText = detail => {
    if (!detail) return '';
    if (lang === 'de' && detail.titleDe && detail.titleDe !== FALLBACK_GERMAN_TEXT) {
      return detail.titleDe;
    }
    return detail.title || detail.titleEn || '';
  };

  const getDescriptionText = detail => {
    if (!detail) return '';
    if (lang === 'de' && detail.description_de && detail.description_de !== FALLBACK_GERMAN_TEXT) {
      return detail.description_de;
    }
    return detail.description || detail.descriptionEn || '';
  };

  const daysFromProgramDetails =
    Array.isArray(programDetailsEntries) && programDetailsEntries.length > 0
      ? programDetailsEntries
          .map((detail, index) =>
            parseDayEntry(detail, index + 1, getTitleText, getDescriptionText)
          )
          .filter(day => day && (day.label || day.titlePart || day.text))
      : [];

  const days =
    daysFromProgramDetails.length > 0
      ? daysFromProgramDetails
      : parseDaysFromActivitiesText(activitiesText);

  if (days.length === 0) {
    return null;
  }

  return (
    <div
      className={embedded ? css.embeddedSection : css.section}
      data-elbaction="visible:vis weeklyPlan"
    >
      <div className={css.header}>
        <div className={css.title}>
          <FormattedMessage id="WeeklyPlan.title" />
        </div>
        {startInfo && <span className={css.badge}>{startInfo}</span>}
      </div>
      <div className={css.timeline}>
        <div className={css.timelineLine} aria-hidden="true" />
        {days.map((day, index) => {
          const isLastDay = index === days.length - 1;

          return (
            <div key={day.dayNumber} className={css.dayItem}>
              <span className={isLastDay ? css.dayDotLast : css.dayDot} aria-hidden="true" />
              <div className={css.dayTitle}>
                <DayTitle day={day} />
              </div>
              {day.text && (
                <div className={css.dayText}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({ children }) => <span>{children}</span>,
                      strong: ({ children }) => <strong>{children}</strong>,
                    }}
                  >
                    {day.text}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WeeklyPlan;
